import * as d3 from 'd3';
import { StatuteStatistic } from '../../types/StatuteStatistics.type';

export type TimeUnit = 'month' | 'quarter' | 'year';

export const MIN_CELL_WIDTH = 28;

export const UNKNOWN_BIN = 'Unknown';
export const TAIL_BIN = 'Tail';
export const UNKNOWN_YEAR = 1900;

export interface TailInfo {
  tailYears: Set<number>;
  tailStart: number;
  tailEnd: number;
}

export function isSpecialBin(bin: string): boolean {
  return bin === UNKNOWN_BIN || bin === TAIL_BIN;
}

export function isUnknownYearMonth(yearMonth: number): boolean {
  return Math.floor(yearMonth / 100) === UNKNOWN_YEAR;
}

export interface RaceColor {
  h: number;
  s: number;
  l: number;
}

export const RACE_COLORS: Record<string, RaceColor> = {
  White: { h: 170, s: 0.25, l: 0.43 },
  Latinx: { h: 324, s: 0.47, l: 0.33 },
  Asian: { h: 353, s: 0.63, l: 0.46 },
  Black: { h: 22, s: 0.78, l: 0.55 },
};

export function yearMonthToBin(yearMonth: number, unit: TimeUnit): string {
  const year = Math.floor(yearMonth / 100);
  const month = yearMonth % 100;
  if (unit === 'year') return `${year}`;
  if (unit === 'quarter') return `${year}-Q${Math.ceil(month / 3)}`;
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function dateToBin(date: Date, unit: TimeUnit): string {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  if (unit === 'year') return `${year}`;
  if (unit === 'quarter') return `${year}-Q${Math.ceil(month / 3)}`;
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function formatBinLabel(bin: string, unit: TimeUnit, tailInfo?: TailInfo | null): string {
  if (bin === UNKNOWN_BIN) return 'Unknown';
  if (bin === TAIL_BIN && tailInfo) {
    return tailInfo.tailStart === tailInfo.tailEnd
      ? `${tailInfo.tailStart}`
      : `${tailInfo.tailStart}–${tailInfo.tailEnd}`;
  }
  if (unit === 'year') return bin;
  if (unit === 'quarter') return bin;
  // month: "2022-03" → "Mar '22"
  const [y, m] = bin.split('-');
  const dt = new Date(Number(y), Number(m) - 1);
  return d3.timeFormat("%b '%y")(dt);
}

export function detectTailYears(stats: StatuteStatistic[]): TailInfo | null {
  // Collect distinct stages per year, excluding the unknown-date sentinel.
  const stagesByYear = new Map<number, Set<string>>();
  stats.forEach((s) => {
    if (s.stage === null) return;
    if (isUnknownYearMonth(s.year_month)) return;
    const year = Math.floor(s.year_month / 100);
    if (!stagesByYear.has(year)) stagesByYear.set(year, new Set());
    stagesByYear.get(year)!.add(s.stage);
  });

  const sortedYears = [...stagesByYear.keys()].sort((a, b) => a - b);
  if (sortedYears.length < 3) return null;

  const maxStages = Math.max(...[...stagesByYear.values()].map((s) => s.size));
  if (maxStages <= 1) return null;

  // Detect the tail and main data
  const mainStartingIndex = sortedYears.findIndex((y) => stagesByYear.get(y)!.size >= maxStages);
  if (mainStartingIndex <= 0) return null;

  const tailYears = sortedYears.slice(0, mainStartingIndex);
  return {
    tailYears: new Set(tailYears),
    tailStart: tailYears[0],
    tailEnd: tailYears[tailYears.length - 1],
  };
}

function countUniqueBins(stats: StatuteStatistic[], unit: TimeUnit): number {
  const bins = new Set<string>();
  stats.forEach((s) => bins.add(yearMonthToBin(s.year_month, unit)));
  return bins.size;
}

export function chooseTimeUnit(stats: StatuteStatistic[], availableWidth: number): TimeUnit {
  const monthBins = countUniqueBins(stats, 'month');
  if (monthBins * MIN_CELL_WIDTH <= availableWidth) return 'month';

  const quarterBins = countUniqueBins(stats, 'quarter');
  if (quarterBins * MIN_CELL_WIDTH <= availableWidth) return 'quarter';

  return 'year';
}
